import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import { useNotification } from "@/shared/hooks/useNotification";
import { useConfirm } from "@/shared/contexts/confirm-context";
import { useAuthStore } from "@/features/auth/stores/authStore";
import {
  useApprovePaymentMethod,
  useDeactivatePaymentMethod,
  useMyPaymentMethodSubmissions,
  usePaymentMethods,
  usePendingPaymentMethods,
  useProvisionPaymentMethod,
  useRejectPaymentMethod,
} from "../hooks/usePaymentMethods";
import {
  PAYMENT_METHOD_APPROVAL_STATUS,
  PAYMENT_METHOD_TYPE,
  type PaymentMethodAccount,
} from "../services/paymentMethodsApi";
import { PaymentMethodForm } from "../components/PaymentMethodForm";
import { RejectPaymentMethodDialog } from "../components/RejectPaymentMethodDialog";

const formatType = (account: PaymentMethodAccount) => {
  switch (account.type) {
    case PAYMENT_METHOD_TYPE.MpesaTill:
      return `Mpesa Till${account.tillNumber ? ` · ${account.tillNumber}` : ""}`;
    case PAYMENT_METHOD_TYPE.MpesaPaybill:
      return `Mpesa Paybill${
        account.paybillNumber ? ` · ${account.paybillNumber}` : ""
      }`;
    case PAYMENT_METHOD_TYPE.Bank:
      return `Bank${account.bankName ? ` · ${account.bankName}` : ""}`;
    default:
      return "Cash";
  }
};

const StatusBadge = ({ account }: { account: PaymentMethodAccount }) => {
  if (account.approvalStatus === PAYMENT_METHOD_APPROVAL_STATUS.Approved) {
    return account.isActive ? (
      <Badge variant="default">Approved</Badge>
    ) : (
      <Badge variant="secondary">Inactive</Badge>
    );
  }
  if (account.approvalStatus === PAYMENT_METHOD_APPROVAL_STATUS.Pending) {
    return <Badge variant="outline">Pending</Badge>;
  }
  return <Badge variant="destructive">Rejected</Badge>;
};

const AccountsTable = ({
  accounts,
  emptyMessage,
  showApprovalActions = false,
  onApprove,
  onReject,
  onDeactivate,
  approvingId,
  deactivatingId,
}: {
  accounts: PaymentMethodAccount[];
  emptyMessage: string;
  showApprovalActions?: boolean;
  onApprove?: (account: PaymentMethodAccount) => void;
  onReject?: (account: PaymentMethodAccount) => void;
  onDeactivate?: (account: PaymentMethodAccount) => void;
  approvingId?: string;
  deactivatingId?: string;
}) => {
  if (accounts.length === 0) {
    return <p className="text-muted-foreground py-6 text-sm">{emptyMessage}</p>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Code</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Provider</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Submitted by</TableHead>
          {(showApprovalActions || onDeactivate) && (
            <TableHead className="text-right">Actions</TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {accounts.map((account) => (
          <TableRow key={account.id}>
            <TableCell className="font-medium">{account.name}</TableCell>
            <TableCell className="font-mono text-xs">{account.code}</TableCell>
            <TableCell>{formatType(account)}</TableCell>
            <TableCell>{account.provider ?? "—"}</TableCell>
            <TableCell>
              <StatusBadge account={account} />
            </TableCell>
            <TableCell className="text-sm">
              {account.requestedByUserName}
            </TableCell>
            {(showApprovalActions || onDeactivate) && (
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  {showApprovalActions &&
                    account.approvalStatus ===
                      PAYMENT_METHOD_APPROVAL_STATUS.Pending && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => onApprove?.(account)}
                          disabled={approvingId === account.id}
                        >
                          {approvingId === account.id ? "..." : "Approve"}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => onReject?.(account)}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                  {onDeactivate &&
                    account.approvalStatus ===
                      PAYMENT_METHOD_APPROVAL_STATUS.Approved &&
                    account.isActive && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onDeactivate(account)}
                        disabled={deactivatingId === account.id}
                      >
                        {deactivatingId === account.id ? "..." : "Deactivate"}
                      </Button>
                    )}
                </div>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export const PaymentMethodsPage = () => {
  const notification = useNotification();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canApprove = hasPermission("payment_methods.approve");
  const canProvision = hasPermission("payment_methods.provision");
  const canDeactivate = hasPermission("payment_methods.deactivate");
  const canConfigureGlAccount = hasPermission("accounts.view");

  const [tab, setTab] = useState<"approved" | "mine" | "pending">("approved");
  const [formOpen, setFormOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<PaymentMethodAccount | null>(
    null
  );

  const approvedQuery = usePaymentMethods();
  const myQuery = useMyPaymentMethodSubmissions({ enabled: canProvision });
  const pendingQuery = usePendingPaymentMethods({ enabled: canApprove });

  const provision = useProvisionPaymentMethod();
  const approve = useApprovePaymentMethod();
  const reject = useRejectPaymentMethod();
  const deactivate = useDeactivatePaymentMethod();
  const confirm = useConfirm();

  const handleProvision = (
    payload: Parameters<typeof provision.mutate>[0]
  ) => {
    provision.mutate(payload, {
      onSuccess: () => {
        notification.success("Payment method submitted for GM approval");
        setFormOpen(false);
      },
      onError: (err: unknown) => {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? "Failed to submit payment method";
        notification.error(message);
      },
    });
  };

  const handleApprove = (account: PaymentMethodAccount) => {
    approve.mutate(
      { id: account.id, payload: {} },
      {
        onSuccess: () => notification.success(`Approved ${account.name}`),
        onError: () => notification.error("Failed to approve payment method"),
      }
    );
  };

  const handleReject = (rejectionReason: string) => {
    if (!rejectTarget) return;
    reject.mutate(
      { id: rejectTarget.id, payload: { rejectionReason } },
      {
        onSuccess: () => {
          notification.success(`Rejected ${rejectTarget.name}`);
          setRejectTarget(null);
        },
        onError: () => notification.error("Failed to reject payment method"),
      }
    );
  };

  const handleDeactivate = (account: PaymentMethodAccount) => {
    confirm({
      title: "Deactivate payment method?",
      description: `${account.name} will no longer appear in the payment dropdown.`,
      variant: "destructive",
      confirmLabel: "Deactivate",
      onConfirm: () =>
        deactivate
          .mutateAsync(account.id)
          .then(() => notification.success(`Deactivated ${account.name}`))
          .catch((e) => {
            notification.error("Failed to deactivate payment method");
            throw e;
          }),
    });
  };

  const isLoading =
    approvedQuery.isLoading ||
    (canProvision && myQuery.isLoading) ||
    (canApprove && pendingQuery.isLoading);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Payment Methods</h1>
          <p className="text-muted-foreground text-sm">
            Cash drawers, Mpesa tills/paybills and bank accounts. New entries are
            approved by the General Manager before they can be used to record
            payments.
          </p>
        </div>
        {canProvision && (
          <Button onClick={() => setFormOpen(true)}>Submit Payment Method</Button>
        )}
      </div>

      {isLoading ? (
        <LoadingAnimation />
      ) : (
        <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
          <TabsList>
            <TabsTrigger value="approved">
              Approved ({approvedQuery.data?.length ?? 0})
            </TabsTrigger>
            {canProvision && (
              <TabsTrigger value="mine">
                My submissions ({myQuery.data?.length ?? 0})
              </TabsTrigger>
            )}
            {canApprove && (
              <TabsTrigger value="pending">
                Pending approval ({pendingQuery.data?.length ?? 0})
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="approved">
            <Card>
              <CardHeader>
                <CardTitle>Approved & active</CardTitle>
                <CardDescription>
                  These accounts appear in the payment-recording dropdown.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AccountsTable
                  accounts={approvedQuery.data ?? []}
                  emptyMessage="No approved payment methods yet."
                  onDeactivate={canDeactivate ? handleDeactivate : undefined}
                  deactivatingId={
                    deactivate.isPending
                      ? (deactivate.variables as string | undefined)
                      : undefined
                  }
                />
              </CardContent>
            </Card>
          </TabsContent>

          {canProvision && (
            <TabsContent value="mine">
              <Card>
                <CardHeader>
                  <CardTitle>My submissions</CardTitle>
                  <CardDescription>
                    Track the status of payment methods you provisioned.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <AccountsTable
                    accounts={myQuery.data ?? []}
                    emptyMessage="You haven't submitted any payment methods."
                  />
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {canApprove && (
            <TabsContent value="pending">
              <Card>
                <CardHeader>
                  <CardTitle>Pending approval</CardTitle>
                  <CardDescription>
                    Review and approve or reject submitted payment methods.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <AccountsTable
                    accounts={pendingQuery.data ?? []}
                    emptyMessage="No pending submissions."
                    showApprovalActions
                    onApprove={handleApprove}
                    onReject={(a) => setRejectTarget(a)}
                    approvingId={
                      approve.isPending
                        ? (approve.variables as { id: string } | undefined)?.id
                        : undefined
                    }
                  />
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      )}

      {formOpen && (
        <PaymentMethodForm
          open
          onOpenChange={setFormOpen}
          isSubmitting={provision.isPending}
          canConfigureGlAccount={canConfigureGlAccount}
          onSubmit={handleProvision}
        />
      )}

      <RejectPaymentMethodDialog
        open={rejectTarget !== null}
        onOpenChange={(open) => !open && setRejectTarget(null)}
        paymentMethodName={rejectTarget?.name}
        isSubmitting={reject.isPending}
        onSubmit={handleReject}
      />
    </div>
  );
};
